import { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { CurrencyProvider } from "@/i18n/CurrencyContext";
import Home from "@/pages/Home";

// Code splitting: solo Home va en el bundle inicial; el resto carga bajo demanda.
const Admin = lazy(() => import("@/pages/Admin"));
const Login = lazy(() => import("@/pages/Login"));
const DepartureDetail = lazy(() => import("@/pages/DepartureDetail"));
const GroupTrips = lazy(() => import("@/pages/GroupTrips"));
const PlanDetail = lazy(() => import("@/pages/PlanDetail"));
const Quiz = lazy(() => import("@/pages/Quiz"));
const Brasil = lazy(() => import("@/pages/Brasil"));
const BrasilDestination = lazy(() => import("@/pages/BrasilDestination"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const ConciergeCasamentos = lazy(() => import("@/pages/ConciergeCasamentos"));
const LuaDeMel = lazy(() => import("@/pages/LuaDeMel"));
const MiViaje = lazy(() => import("@/pages/MiViaje"));
const ResourcePage = lazy(() => import("@/pages/ResourcePage"));

function App() {
  useEffect(() => {
    // Lenis (scroll suave) se carga despues del primer paint para no competir
    // con el arranque; la pagina scrollea nativo hasta que monta.
    let lenis, raf, cancelled = false;
    const start = () => {
      if (cancelled) return;
      import("lenis").then(({ default: Lenis }) => {
        if (cancelled) return;
        lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        const loop = (time) => {
          lenis.raf(time);
          raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
      }).catch(() => {
        // Si el chunk no carga (p.ej. pestaña abierta durante un deploy),
        // la pagina sigue con scroll nativo, sin romper nada.
      });
    };
    const idle = window.requestIdleCallback ? window.requestIdleCallback(start, { timeout: 2500 }) : setTimeout(start, 1200);
    // La "boot shell" del HTML cubre la carga inicial; la retiramos cuando
    // el hero real ya montó y terminó su animación de entrada.
    const shell = document.getElementById("boot-shell");
    let t1, t2;
    if (shell) {
      t1 = setTimeout(() => shell.classList.add("bs-hide"), 450);
      t2 = setTimeout(() => shell.remove(), 950);
    }
    return () => {
      cancelled = true;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      cancelAnimationFrame(raf);
      lenis?.destroy();
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <div className="App">
      <HelmetProvider>
      <LanguageProvider>
        <CurrencyProvider>
        <BrowserRouter>
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF6EE" }} />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/viaje-a-medida" element={<PlanDetail />} />
          <Route path="/brasil" element={<Brasil />} />
          <Route path="/brasil/:slug" element={<BrasilDestination />} />
          <Route path="/recursos/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/viajes-en-grupo" element={<GroupTrips />} />
            <Route path="/concierge-casamentos" element={<ConciergeCasamentos />} />
            <Route path="/lua-de-mel" element={<LuaDeMel />} />
            <Route path="/salida/:id" element={<DepartureDetail />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/mi-viaje" element={<MiViaje />} />
            <Route path="/recursos/blog" element={<ResourcePage kind="blog" />} />
            <Route path="/recursos/descargables" element={<ResourcePage kind="downloads" />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        </CurrencyProvider>
      </LanguageProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
